import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import type { PanelStickyPropsFn } from './stickyPanel'

/** Measure a sticky page header and return props for panel headers that pin below it. */
export const useStickyPanelOffset = (): {
  pageHeaderRef: RefObject<HTMLDivElement>
  panelStickyProps: PanelStickyPropsFn
} => {
  const pageHeaderRef = useRef<HTMLDivElement>(null)
  const [panelStickyTop, setPanelStickyTop] = useState<number | undefined>(undefined)

  useEffect(() => {
    const el = pageHeaderRef.current
    if (el == null) return
    const sync = (): void => setPanelStickyTop(Math.ceil(el.getBoundingClientRect().height))
    sync()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const panelStickyProps = useMemo(
    (): PanelStickyPropsFn =>
      (stickyZIndex: number) =>
        panelStickyTop == null ? {} : { stickyOffset: panelStickyTop, stickyZIndex },
    [panelStickyTop]
  )

  return { pageHeaderRef, panelStickyProps }
}
